import { useState, useMemo } from 'react';
import { keyBy } from 'lodash';
import {
  Box,
  CardContent,
  Card,
  Divider,
  Skeleton,
  styled,
} from '@mui/material';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import InvestmentInflowChart from './InvestmentInflowChart';
import useInvestmentInflowSummary from 'hooks/data/dashboards/useInvestmentInflowSummary';
import useCurrentValueAumByDate from 'hooks/data/dashboards/useCurrentValueAumByDate';
import Heading from 'components/Heading';
import { DateRangeKey } from 'further-types/dashboard';
import LoadingError from '../LoadingError';
import useInitialElementSize from 'hooks/ui/useInitialElementSize';
import { useFundTypeSelectorOptionsContext } from '../FundTypeSelector/FundTypeSelectorOptionsContext';
import FundTypeSelector from '../FundTypeSelector';
import { useSelectedFundTypeState } from '../FundTypeSelector/useSelectedFundTypeState';
import { Row } from 'components/Layout';

const LoadingSkeletonContainer = styled('div')`
  height: 100%;
  padding: 20px 0;
`;

const FiltersRow = styled(Row)(({ theme }) => ({
  maxWidth: '300px',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '350px',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: '250px',
  },
}));

const InvestmentsInflowSummary: React.FC<{ firmId: string }> = ({ firmId }) => {
  const { options: fundTypeOptions } = useFundTypeSelectorOptionsContext();
  const {
    selectedFundTypes,
    allFundsSelected,
    setSelectedFundTypes,
    selectedFundTypeTagIds,
  } = useSelectedFundTypeState(fundTypeOptions);

  const dateRangelabels = [
    { title: 'All time', slug: DateRangeKey.ALL_TIME, renderCurrentAum: true },
    {
      title: 'Year to date',
      slug: DateRangeKey.THIS_YEAR,
      renderCurrentAum: false,
    },
    {
      title: 'UK tax year to date',
      slug: DateRangeKey.THIS_UK_TAX_YEAR,
      renderCurrentAum: false,
    },
    {
      title: 'Past year',
      slug: DateRangeKey.PAST_YEAR,
      renderCurrentAum: true,
    },
    {
      title: 'Past UK tax year',
      slug: DateRangeKey.PAST_UK_TAX_YEAR,
      renderCurrentAum: false,
    },
    {
      title: 'Past 6 months',
      slug: DateRangeKey.PAST_SIX_MONTHS,
      renderCurrentAum: false,
    },
    {
      title: 'Past quarter',
      slug: DateRangeKey.PAST_QUARTER,
      renderCurrentAum: false,
    },
    {
      title: 'Past month',
      slug: DateRangeKey.PAST_MONTH,
      renderCurrentAum: false,
    },
    {
      title: 'Past week',
      slug: DateRangeKey.PAST_WEEK,
      renderCurrentAum: false,
    },
  ];

  const [selectedDateRange, setSelectedDateRange] = useState(
    dateRangelabels[0].slug,
  );
  const {
    data: investmentInflowSummary,
    isFetching: isFetchingInvestmentInflowSummary,
    isRefetching: isRefetchingInvestmentInflowSummary,
    isError: isErrorInvestmentInflowSummary,
  } = useInvestmentInflowSummary({
    firmId,
    fundTagIds: allFundsSelected ? [] : selectedFundTypeTagIds,
    dateRangeKey: selectedDateRange,
  });

  const renderCurrentAum = dateRangelabels.find(
    (label) => label.slug === selectedDateRange,
  )?.renderCurrentAum;

  const {
    data: currentValueAumByDate,
    isFetching: isFetchingCurrentValueAumByDate,
    isRefetching: isRefetchingCurrentValueAumByDate,
    isError: isErrorCurrentValueAumByDate,
  } = useCurrentValueAumByDate({
    firmId,
    fundTagIds: allFundsSelected ? [] : selectedFundTypeTagIds,
    dateRangeKey: selectedDateRange,
    enabled: renderCurrentAum,
  });

  const mergedData = useMemo(() => {
    const inflowData = investmentInflowSummary?.inflowByDate ?? [];
    const aumData = currentValueAumByDate ?? [];
    const aumByDate = keyBy(aumData, 'date');

    const mergedWithInflow = inflowData.map((item) => ({
      ...item,
      currentValueAum: aumByDate[item.date]?.currentValueAum ?? 0,
    }));

    // Only add AUM-only entries for monthly intervals
    const isMonthlyInterval = [
      DateRangeKey.ALL_TIME,
      DateRangeKey.PAST_YEAR,
    ].includes(selectedDateRange);

    if (!aumData.length || !isMonthlyInterval) return mergedWithInflow;

    const inflowDates = new Set(inflowData.map((item) => item.date));
    const lastCumulative = inflowData[inflowData.length - 1] ?? {
      investmentAmountCumulative: 0,
      investorsCumulative: 0,
    };

    const aumOnlyEntries = aumData
      .filter((item) => !inflowDates.has(item.date))
      .map((item) => ({
        date: item.date,
        investmentAmount: 0,
        investmentAmountCumulative: lastCumulative.investmentAmountCumulative,
        investors: 0,
        investorsCumulative: lastCumulative.investorsCumulative,
        currentValueAum: item.currentValueAum ?? 0,
      }));

    return [...mergedWithInflow, ...aumOnlyEntries].sort((a, b) =>
      a.date.localeCompare(b.date),
    );
  }, [investmentInflowSummary, currentValueAumByDate, selectedDateRange]);

  const dataSource = mergedData || {
    inflowByDate: [],
    investmentAmount: 0,
    investors: 0,
    currentValueAum: 0,
  };

  const isLoading =
    isFetchingInvestmentInflowSummary ||
    isFetchingCurrentValueAumByDate ||
    isRefetchingInvestmentInflowSummary ||
    isRefetchingCurrentValueAumByDate;

  const [ref, { height }] = useInitialElementSize();

  if (isErrorInvestmentInflowSummary && isErrorCurrentValueAumByDate) {
    return <LoadingError height={height} />;
  }

  return (
    <Card ref={ref}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          marginBottom={5}
          alignItems="center"
        >
          <Row centered spacing="sm" justify="space-between" fullWidth>
            <Heading variant="h3" subtitle="All tranches" noMargin>
              Lifetime fundraise record
            </Heading>
            <FiltersRow fullWidth spacing="sm">
              <FundTypeSelector
                allOptionsSelected={allFundsSelected}
                options={fundTypeOptions}
                value={selectedFundTypes}
                onChange={(newSelectedFundType) =>
                  setSelectedFundTypes(newSelectedFundType)
                }
              />
              <AppSelectBox
                fullWidth
                data={dateRangelabels}
                label="Date range"
                valueKey="slug"
                variant="outlined"
                labelKey="title"
                value={selectedDateRange}
                onChange={(e) =>
                  setSelectedDateRange(e.target.value as DateRangeKey)
                }
              />
            </FiltersRow>
          </Row>
        </Box>
        <Divider />
        <Box
          display="flex"
          flexDirection="column"
          position="relative"
          zIndex={1}
          flexBasis="auto"
          flexGrow={1}
          flexShrink={1}
        >
          {isLoading && (
            <LoadingSkeletonContainer>
              <Skeleton
                variant="rounded"
                height="100%"
                width="100%"
                animation="wave"
              />
              <Skeleton variant="text" width="100%" animation="wave" />
            </LoadingSkeletonContainer>
          )}
          {!isLoading && (
            <InvestmentInflowChart
              data={dataSource}
              isFetchingUnrealisedValue={isFetchingCurrentValueAumByDate}
              renderCurrentAum={
                renderCurrentAum && !isErrorCurrentValueAumByDate
              }
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default InvestmentsInflowSummary;
